import React, { useState } from "react";
import { Box, Button, Anchor, Text } from "grommet";
import { gql, useQuery } from '@apollo/client';

import TTJsonTree from "../../common/TTJsonTree";
import HealthStatusServiceDataView from "../../health2/details/HealthStatusServiceDataView";
import ServiceHistoryFindNow from "../ServiceHistoryFindNow";

import { SessionParser } from "../Parser";
import { TopBar } from "../../triage/TriageViewUtils";

import Spinner from "../../common/Spinner";
import TTabSub from "../../common/TTabSub";
import TTCopyClipboard from '../../common/TTCopyClipboard'
import envConfig from '../../../utils/envConfig'

const JSON_DATA = gql`
  query($instance: String!, $timestamp: timestamp!) {
    zkdata(
      where: { instance: { _eq: $instance }, timestamp: { _eq: $timestamp } }
    ) {
      data
    }
  }
`;

const liteMode = envConfig[process.env.REACT_APP_ENVIRONMENT]['lite']

function parseBouncerStatus(status) {
    if (Number.isInteger(status)) {
        return status === 0 ? "UP" : status === 2 ? "DISABLE" : "DOWN"
    } else {
        return status
    }
}

export const ServiceHistoryDetailPostgres = ({ instance, timestamp, host, env, service, showLink = true }) => {

    let sessionData = ""
    let sessionType = ""
    let urlLink = ""

    if (showLink) {
        urlLink = '/servicehistory/' + host + "/" + service + "/" + timestamp + "/" + instance + "?env=" + env
    }

    const resultHistoryJson = useQuery(JSON_DATA, {
        variables: { instance: instance, timestamp: timestamp },
    });
    const [selectedSubTab, setSelectedSubTab] = useState("Point in Time");

    /// Parse Data for Extended Data View
    if (
        resultHistoryJson &&
        !resultHistoryJson.loading &&
        resultHistoryJson.data &&
        resultHistoryJson.data.zkdata &&
        resultHistoryJson.data.zkdata.length > 0
    ) {
        const zkdata = resultHistoryJson.data.zkdata[0].data;

        if (service === "ase") {
            sessionType = "ase";
            sessionData = SessionParser(sessionType, zkdata);
        } else if (
            service === "algoserver_exec" ||
            service === "algoserver_debug" ||
            service === "algo_ng_exec" ||
            service === "algo_ng_canvas"
        ) {
            sessionType = "algoserver_exec";
            sessionData = SessionParser(sessionType, zkdata);

        } else if (service === "bookie") {
            sessionType = "bookie";
            if (zkdata.custom_data && zkdata.custom_data.cluster_information) {
                sessionData = zkdata.custom_data.cluster_information.connections || [];
            }
        } else if (service === "juno_up") {
            sessionType = "accounts";
            if (zkdata.custom_data) {
                let accounts = [];
                if (zkdata.custom_data.cluster_information && zkdata.custom_data.cluster_information.accounts) {
                    zkdata.custom_data.cluster_information.accounts.forEach((a) => {
                        let each_acct = {
                            id: a.id,
                            status: a.status
                        };
                        accounts.push(each_acct);
                    });
                }
                sessionData = accounts;
            }
        } else if (service === "bouncer") {
            sessionType = "accounts";
            if (zkdata.custom_data) {
                let accounts = [];
                if (zkdata.custom_data.accounts) {
                    zkdata.custom_data.accounts.forEach((a) => {
                        let each_acct = {
                            primary: true,
                            markets: a.markets,
                            id: a.id,
                            reason: a.reason,
                            status: parseBouncerStatus(a.status),
                        };
                        accounts.push(each_acct);
                    });
                }

                if (zkdata.custom_data.secondary_accounts) {
                    zkdata.custom_data.secondary_accounts.forEach((a) => {
                        let each_acct = {
                            primary: false,
                            markets: a.markets,
                            id: a.id,
                            reason: a.reason,
                            status: parseBouncerStatus(a.status)
                        };
                        accounts.push(each_acct);
                    });
                }
                sessionData = accounts;
            }
        } else if (service === "stager") {
            sessionType = "stager";
            if (zkdata.custom_data) {
                let accounts = [];
                if (zkdata.custom_data.accounts) {
                    zkdata.custom_data.accounts.forEach((a) => {
                        let each_acct = {
                            id: a.id,
                            primary: true,
                            markets: a.markets,
                            reason: a.reason,
                            status: a.status,
                        };
                        accounts.push(each_acct);
                    });
                }
                if (zkdata.custom_data.secondary_accounts) {
                    zkdata.custom_data.secondary_accounts.forEach((a) => {
                        let each_acct = {
                            id: a.id,
                            primary: false,
                            markets: a.markets,
                            reason: a.reason,
                            status: a.status,
                        };
                        accounts.push(each_acct);
                    });
                }
                sessionData = accounts;
            }
        } else if (service === "imbookie") {
            sessionType = "imbookie";
            if (zkdata.custom_data && zkdata.custom_data.cluster_information) {
                sessionData = zkdata.custom_data.cluster_information.connections || [];
            }
        } else if (zkdata.custom_data && zkdata.custom_data.fix_session_status) {
            sessionType = "fixsessions";
            sessionData = zkdata.custom_data.fix_session_status;
        } else if (zkdata.custom_data && zkdata.custom_data.connections) {
            sessionType = "connections";
            sessionData = zkdata.custom_data.connections;
            // user_sessions in zkdata because unparsed
        } else if (zkdata.custom_data && zkdata.custom_data.user_sessions) {
            sessionType = "users";
            sessionData = zkdata.custom_data.user_sessions;
        }
    }

    return (
        <Box width={{ min: "800px" }}>
            {resultHistoryJson && resultHistoryJson.loading && <Spinner />}
            {resultHistoryJson &&
                !resultHistoryJson.loading &&
                resultHistoryJson.data &&
                resultHistoryJson.data.zkdata &&
                resultHistoryJson.data.zkdata.length > 0 && (
                    <>
                        <Box
                            direction="row"
                            margin={{ top: "small", left: "xsmall", right: "xsmall" }}
                            fill="horizontal"
                        >
                            <Text
                                color="dark-3"
                                size="small"
                                margin={{ bottom: "medium" }}
                                weight="bold"
                            >
                                Zookeeper Data
                            </Text>

                            {showLink &&
                                <Box direction="row">
                                    <Box
                                        pad="none"
                                        gap="none"
                                        margin={{ left: "10px" }}
                                        background="brand"
                                        round="xsmall"
                                        height="25px"
                                    >
                                        <Anchor margin={{ left: "6px", right: "6px" }} href={urlLink} target="_blank">
                                            <Button plain label={<Text color="white" size="12px" weight="normal">Sharable Link</Text>} margin={{ top: "xxsmall" }} hoverIndicator={{ color: "light-1", opacity: "weak" }} />
                                        </Anchor>
                                    </Box>
                                    <Box
                                        pad="none"
                                        gap="none"
                                        height="25px"
                                        margin={{ left: "5px" }}
                                    >
                                        <TTCopyClipboard copyText={window.location.protocol + '//' + window.location.host + urlLink} />
                                    </Box>
                                </Box>
                            }

                        </Box>
                        <Box
                            overflow={{ vertical: "auto", horizontal: "hidden" }}
                            direction="row"
                            wrap={true}
                            fill="horizontal"
                        >
                            {!liteMode &&
                                <TopBar
                                    text={JSON.stringify(resultHistoryJson.data.zkdata[0].data)}
                                />
                            }
                            <TTJsonTree data={resultHistoryJson.data.zkdata[0].data} />

                            {sessionData && Object.keys(sessionData).length > 0 && (
                                <Box
                                    basis="1"
                                    gap="none"
                                    pad="none"
                                    direction="row"
                                    align="start"
                                    justify="start"
                                    margin={{ left: "7px" }}
                                >
                                    <TTabSub
                                        subTabName={"Sessions At Point in Time"}
                                        handleClickSubTab={() => setSelectedSubTab("Point in Time")}
                                        selected={selectedSubTab === "Point in Time"}
                                    />
                                    {(sessionType === "connections" ||
                                        sessionType === "fixsessions" ||
                                        //sessionType === "accounts" ||
                                        sessionType === "users") && (
                                            <TTabSub
                                                subTabName="Where are the Sessions Now"
                                                handleClickSubTab={() => setSelectedSubTab("Now")}
                                                selected={selectedSubTab === "Now"}
                                            />
                                        )}
                                </Box>
                            )}

                            {sessionData &&
                                sessionType &&
                                selectedSubTab === "Point in Time" && (
                                    <Box fill="horizontal" hight="600px">
                                        <HealthStatusServiceDataView
                                            sessionData={sessionData}
                                            sessionType={sessionType}
                                            nodeName={host}
                                            envName={env}
                                        />
                                    </Box>
                                )}

                            {sessionData &&
                                sessionType &&
                                selectedSubTab === "Now" &&
                                (sessionType === "connections" ||
                                    sessionType === "fixsessions" ||
                                    //sessionType === "accounts" ||
                                    sessionType === "users") && (
                                    <Box fill="horizontal" hight="600px">
                                        <ServiceHistoryFindNow
                                            sessionDataPast={sessionData}
                                            sessionType={sessionType}
                                            envName={env}
                                        />
                                    </Box>
                                )}
                        </Box>
                    </>
                )}
        </Box>
    );
};